// Domain: Role Service
// Handles role-based logic, permissions, and utilities

import { ROLES, PERMISSIONS, getRolePermissions } from "./role.constants";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../services/firebase";

class RoleService {
    constructor() {
        this.currentUser = null;
        this.userRole = null;
        this.userPermissions = [];
    }

    // Initialize role service with current user
    async initialize(user) {
        if (!user) {
            this.currentUser = null;
            this.userRole = null;
            this.userPermissions = [];
            return;
        }

        this.currentUser = user;
        await this.loadUserRole();
    }

    // Load user role from Firestore
    async loadUserRole() {
        try {
            if (!this.currentUser?.uid) return;

            const userDoc = await getDoc(doc(db, "users", this.currentUser.uid));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                this.userRole = userData.role || ROLES.INVESTOR;
                this.userPermissions = getRolePermissions(this.userRole);
            } else {
                // Default role for new users
                this.userRole = ROLES.INVESTOR;
                this.userPermissions = getRolePermissions(this.userRole);
            }
        } catch (error) {
            this.userRole = ROLES.INVESTOR;
            this.userPermissions = getRolePermissions(this.userRole);
        }
    }

    // Get current user role
    getCurrentUserRole() {
        return this.userRole;
    }

    // Get current user permissions
    getCurrentUserPermissions() {
        return this.userPermissions;
    }

    // Check if current user has specific permission
    hasPermission(permission) {
        return this.userPermissions.includes(permission);
    }

    // Check if current user has any of the specified permissions
    hasAnyPermission(permissions) {
        return permissions.some(permission => this.hasPermission(permission));
    }

    // Check if current user has all specified permissions
    hasAllPermissions(permissions) {
        return permissions.every(permission => this.hasPermission(permission));
    }

    // Check if user can access opportunity (based on assignment or role)
    canAccessOpportunity(opportunity) {
        if (!opportunity) return false;

        // Admin can access all opportunities
        if (this.userRole === ROLES.ADMIN) return true;

        // Agent can access their own opportunities
        if (this.userRole === ROLES.AGENT && opportunity.agentId === this.currentUser?.uid) {
            return true;
        }

        // Legal, Reviewer, Analyst can access all opportunities for review
        if ([ROLES.LEGAL, ROLES.REVIEWER, ROLES.ANALYST].includes(this.userRole)) {
            return true;
        }

        // Investor can only access published opportunities
        if (this.userRole === ROLES.INVESTOR && opportunity.status === "published") {
            return true;
        }

        return false;
    }

    // Check if user can edit opportunity
    canEditOpportunity(opportunity) {
        if (!this.hasPermission(PERMISSIONS.EDIT_OPPORTUNITY)) return false;

        // Admin can edit all opportunities
        if (this.userRole === ROLES.ADMIN) return true;

        // Agent can edit their own opportunities
        if (this.userRole === ROLES.AGENT && opportunity.agentId === this.currentUser?.uid) {
            return true;
        }

        // Analyst can edit opportunities for analysis
        if (this.userRole === ROLES.ANALYST) return true;

        return false;
    }

    // Check if user can approve opportunity
    canApproveOpportunity() {
        return this.hasPermission(PERMISSIONS.APPROVE_OPPORTUNITY);
    }

    // Check if user can manage tasks
    canManageTasks() {
        return this.hasAnyPermission([
            PERMISSIONS.CREATE_TASKS,
            PERMISSIONS.ASSIGN_TASKS,
            PERMISSIONS.UPDATE_TASKS,
        ]);
    }

    // Check if user can view KYC
    canViewKYC() {
        return this.hasPermission(PERMISSIONS.VIEW_KYC);
    }

    // Check if user can approve KYC
    canApproveKYC() {
        return this.hasPermission(PERMISSIONS.APPROVE_KYC);
    }

    // Update user role (admin only)
    async updateUserRole(userId, newRole) {
        if (!this.hasPermission(PERMISSIONS.MANAGE_USERS)) {
            throw new Error("Insufficient permissions to update user role");
        }

        if (!Object.values(ROLES).includes(newRole)) {
            throw new Error("Invalid role");
        }

        await updateDoc(doc(db, "users", userId), {
            role: newRole,
            updatedAt: new Date()
        });

        // Update local state if updating current user
        if (userId === this.currentUser?.uid) {
            this.userRole = newRole;
            this.userPermissions = getRolePermissions(newRole);
        }

        return true;
    }

    // Get available roles for assignment (filtered by current user permissions)
    getAvailableRoles() {
        const roles = Object.values(ROLES);
    
        // Admin can assign all roles
        if (this.userRole === ROLES.ADMIN) {
            return roles;
        }

        // Other roles have limited assignment capabilities
        if (this.userRole === ROLES.REVIEWER) {
            return [ROLES.AGENT, ROLES.ANALYST];
        }

        return [];
    }

    // Get role display name
    getRoleDisplayName(role) {
        const displayNames = {
            [ROLES.ADMIN]: "Administrador",
            [ROLES.AGENT]: "Agente Captador",
            [ROLES.LEGAL]: "Equipo Legal",
            [ROLES.REVIEWER]: "Revisor",
            [ROLES.ANALYST]: "Analista",
            [ROLES.INVESTOR]: "Inversor"
        };

        return displayNames[role] || role;
    }

    // Get role description
    getRoleDescription(role) {
        const descriptions = {
            [ROLES.ADMIN]: "Acceso completo al sistema. Puede gestionar usuarios, oportunidades y configuraciones.",
            [ROLES.AGENT]: "Captador de oportunidades. Puede crear y gestionar sus propias oportunidades.",
            [ROLES.LEGAL]: "Equipo legal. Revisa documentación y valida aspectos legales.",
            [ROLES.REVIEWER]: "Revisor final. Aprueba oportunidades y asigna tareas.",
            [ROLES.ANALYST]: "Analista de inversiones. Evalúa viabilidad y riesgo.",
            [ROLES.INVESTOR]: "Inversor. Puede ver oportunidades publicadas y gestionar su portafolio."
        };

        return descriptions[role] || "Sin descripción disponible";
    }

    // Clear current user data (for logout)
    clearCurrentUser() {
        this.currentUser = null;
        this.userRole = null;
        this.userPermissions = [];
    }
}

// Export singleton instance
export const roleService = new RoleService(); 